import moment from 'moment-timezone';
import { useRegion } from '@/api/rest/users/useRegion';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { useToastContext } from '@payaca/ambrosia';
import EntityCard from '@payaca/components/entityCard/EntityCard';
import { AlertTooltip } from '@payaca/components/iconTooltip/IconTooltip';
import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import {
  DuplicateProposalModal,
  DuplicatedProposal,
} from '../duplicateProposalModal/DuplicateProposalModal';
import JobStatusBadge from '../jobStatusBadge/JobStatusBadge';
import { SentProposalPanelLocationState } from '@/ui/pages/proposalBuilderPage/components/sentProposalPanel/SentProposalPanel';

import {
  useCustomerForDeal,
  useDeal,
  useJobContent,
  useProposal,
} from '@payaca/store/hooks/appState';

import { currencyPrice } from '@payaca/helpers/financeHelper';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { getAvailableActionsForJob } from '@payaca/helpers/jobActionsHelper';
import { getJobType } from '@payaca/helpers/jobHelperV2';

import { JobActionType } from '@payaca/types/jobActionsTypes';

import { actions as jobActions } from '@/api/jobs';
import { usePermissions } from '@/hooks/usePermissions';

import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import './ProposalCard.sass';

interface Props {
  proposalId: number;
  onClick?: () => void;
  onDealUpdateSuccess?: () => void;
}

// NOTE: This card is only useable for proposals on a V2 deal. Using this for a V1 deal proposal will render incorrect quickactions
const ProposalCard: FC<PropsWithChildren<Props>> = ({
  proposalId,
  onClick,
  onDealUpdateSuccess,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { pushError } = useToastContext();
  const { userHasRequiredPermission } = usePermissions();
  const proposal = useProposal(proposalId);
  const jobContent = useJobContent(proposal?.jobContentId);
  const customer = useCustomerForDeal(proposal?.dealId);
  const deal = useDeal(proposal?.dealId);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isMarkingJobAccepted, setIsMarkingJobAccepted] = useState(false);
  const [isMarkingJobDeclined, setIsMarkingJobDeclined] = useState(false);

  const region = useRegion();
  const shortDateRegionalFormat = useMemo(
    () => getInternationalMomentDateFormatByRegion(DateFormats.SHORT, region),
    [region]
  );

  const markJobAccepted = useCallback(
    (proposalId: number) => {
      setIsMarkingJobAccepted(true);
      dispatch(
        jobActions.acceptQuote(proposalId, () => {
          onDealUpdateSuccess?.();
          setIsMarkingJobAccepted(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const markJobDeclined = useCallback(
    (proposalId: number) => {
      setIsMarkingJobDeclined(true);
      dispatch(
        jobActions.declineJob(proposalId, () => {
          onDealUpdateSuccess?.();
          setIsMarkingJobDeclined(false);
        })
      );
    },
    [dispatch, onDealUpdateSuccess]
  );

  const duplicateJob = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const onProposalDuplicated = useCallback(
    (duplicated: DuplicatedProposal) => {
      setShowDuplicateModal(false);
      history.push('/'); // force a remount even when only the path params change
      history.push(
        `/projects/${duplicated.projectPublicId}/proposals/${duplicated.proposalPublicId}`
      );
    },
    [history]
  );

  const goToSendJob = useCallback(() => {
    if (!deal?.publicId || !proposal?.publicId) return;
    history.push(
      `/projects/${deal.publicId}/proposals/${proposal.publicId}/draft-send`
    );
  }, [history, deal?.publicId, proposal?.publicId]);

  const goToResend = useCallback(() => {
    if (!deal?.publicId || !proposal?.publicId) return;
    const state: SentProposalPanelLocationState = { view: 'resend' };
    history.push(
      `/projects/${deal.publicId}/proposals/${proposal.publicId}`,
      state
    );
  }, [history, deal?.publicId, proposal?.publicId]);

  const proposalQuickActionDefinitions: { [key: string]: any } = useMemo(() => {
    let definitions = {};
    if (userHasRequiredPermission([JobPermissions.DUPLICATE_JOB])) {
      definitions = {
        [JobActionType.DUPLICATE]: {
          actionName: 'Duplicate',
          actionBehaviour: duplicateJob,
        },
      };
    }
    definitions = {
      ...definitions,
      [JobActionType.MARK_AS_ACCEPTED]: {
        actionName: 'Mark as accepted',
        actionBehaviour: markJobAccepted,
        isActionProcessing: isMarkingJobAccepted,
      },
      [JobActionType.MARK_AS_DECLINED]: {
        actionName: 'Mark as declined',
        actionBehaviour: markJobDeclined,
        isActionProcessing: isMarkingJobDeclined,
      },
      [JobActionType.SEND]: {
        actionName: 'Send',
        actionBehaviour: goToSendJob,
      },
      [JobActionType.RESEND]: {
        actionName: 'Resend',
        actionBehaviour: goToResend,
      },
    };
    return definitions;
  }, [
    duplicateJob,
    markJobAccepted,
    isMarkingJobAccepted,
    markJobDeclined,
    isMarkingJobDeclined,
    goToSendJob,
    goToResend,
    userHasRequiredPermission,
  ]);

  const availableActions = useMemo(
    () =>
      proposal && deal && customer
        ? getAvailableActionsForJob(proposal, deal, customer)
        : [],
    [proposal, deal, customer]
  );

  const quickActions = useMemo(() => {
    return availableActions
      .map((availableAction) => proposalQuickActionDefinitions[availableAction])
      .filter((x) => !!x);
  }, [availableActions, proposalQuickActionDefinitions]);

  if (!proposal || !jobContent) return null;

  const dateElement = () => {
    if (proposal.validUntil && !proposal.acceptedAt) {
      return (
        <>
          <dt>Valid until</dt>
          <dd>
            <time dateTime={proposal?.validUntil?.toString()}>
              {moment(proposal.validUntil).format(shortDateRegionalFormat)}
            </time>
          </dd>
        </>
      );
    } else if (proposal.acceptedAt) {
      return (
        <>
          <dt>Accepted at</dt>
          <dd>
            <time dateTime={proposal?.acceptedAt?.toString()}>
              {moment(proposal.acceptedAt).format(shortDateRegionalFormat)}
            </time>
          </dd>
        </>
      );
    }
  };

  const issue = (() => {
    if (proposal.bouncedAt) {
      return 'E-mail has bounced';
    }
  })();

  return (
    <>
      <EntityCard
        className="proposal-card"
        onClick={onClick}
        quickActionsConfig={{
          recordId: proposalId,
          renderDisabledIfNoActions: true,
          quickActions: quickActions,
        }}
      >
        <ResponsiveViewWrapper
          className="proposal-card-content"
          downBreakpointSm={500}
        >
          <>
            <JobStatusBadge status={proposal.readableStatus} />
            <dl className="price">
              <dt>
                {getJobType(proposal)}
                {` total (inc tax)`}
              </dt>
              <dd>{currencyPrice(jobContent.total, region)}</dd>
            </dl>
            <dl className="date">{dateElement()}</dl>
            {issue && (
              <div className={'card-issue'}>
                <AlertTooltip tooltipText={issue} />
              </div>
            )}
          </>
        </ResponsiveViewWrapper>
      </EntityCard>
      <DuplicateProposalModal
        isOpen={showDuplicateModal}
        jobId={proposalId}
        onClose={() => setShowDuplicateModal(false)}
        onDuplicated={onProposalDuplicated}
        onError={pushError}
      />
    </>
  );
};

export default ProposalCard;
